import React, { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "cookie-consent-v1";

const setPostHogOptIn = (accepted) => {
  try {
    if (typeof window === "undefined" || !window.posthog) return;
    if (accepted) {
      if (typeof window.posthog.opt_in_capturing === "function") {
        window.posthog.opt_in_capturing();
      }
    } else if (typeof window.posthog.opt_out_capturing === "function") {
      window.posthog.opt_out_capturing();
    }
  } catch (err) {
    console.warn("CookieBanner: failed to update PostHog consent", err);
  }
};

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let stored = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      console.warn("CookieBanner: localStorage unavailable", err);
      stored = null;
    }

    if (!stored) {
      // No decision yet — opt the user OUT of analytics until they choose.
      setPostHogOptIn(false);
      setVisible(true);
    } else {
      // Re-apply their previous choice to PostHog on each load.
      try {
        const parsed = JSON.parse(stored);
        setPostHogOptIn(parsed?.analytics === true);
      } catch (err) {
        console.warn("CookieBanner: corrupted consent value, resetting", err);
        setPostHogOptIn(false);
      }
    }
  }, []);

  const persist = (decision) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          analytics: decision === "accept",
          decidedAt: new Date().toISOString(),
        })
      );
    } catch (err) {
      console.warn("CookieBanner: failed to persist consent", err);
    }
    setPostHogOptIn(decision === "accept");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-[60] animate-in fade-in slide-in-from-bottom-4 duration-300"
      data-testid="cookie-banner"
    >
      <div className="relative bg-white rounded-2xl shadow-2xl ring-1 ring-gray-200 overflow-hidden">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-teal-400 via-teal-500 to-orange-400 rounded-2xl blur opacity-25 pointer-events-none"></div>
        <div className="relative p-5">
          <div className="flex items-start gap-3">
            <div className="bg-teal-600 text-white p-2 rounded-lg flex-shrink-0">
              <Cookie className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-bold text-gray-900 mb-1">
                We value your privacy
              </h2>
              <p className="text-xs text-gray-600 leading-relaxed">
                We use strictly-necessary cookies to make this site work and, with your
                consent, analytics cookies to understand how visitors use it. See our{" "}
                <a
                  href="/cookie-policy"
                  className="text-teal-700 underline hover:text-teal-900 font-medium"
                >
                  Cookie Policy
                </a>{" "}
                and{" "}
                <a
                  href="/privacy-policy"
                  className="text-teal-700 underline hover:text-teal-900 font-medium"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
            <button
              onClick={() => persist("reject")}
              aria-label="Reject cookies"
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              data-testid="cookie-banner-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <button
              onClick={() => persist("reject")}
              className="px-3 py-2 text-sm font-semibold text-teal-700 border border-teal-300 hover:bg-teal-50 rounded-lg transition-colors"
              data-testid="cookie-banner-reject"
            >
              Reject all
            </button>
            <button
              onClick={() => persist("accept")}
              className="px-3 py-2 text-sm font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors shadow-sm"
              data-testid="cookie-banner-accept"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
